import UserMinus from "@/assets/icons/user-minus.svg";
import {
  SEARCH_SUBSCRIPTION_INTERVAL,
  SearchSubscriptionInterval,
} from "@/constants";
import { useSubscriptionHandler } from "@/data-hooks/useSubscriptions";
import { FEATURE } from "@/features";
import { useHasFeature } from "@/hooks/useHasFeature";
import { Button } from "@components/ui/button";
import Clickable from "@components/Clickable";
import {
  Dropdown,
  DropdownCheckboxItem,
  DropdownHeading,
  DropdownItemIndicator,
  DropdownMenu,
  DropdownSeparator,
  DropdownToggle,
} from "@components/Dropdown";
import { CheckboxIndicator } from "@components/Field/Field/Checkbox";
import { i18n } from "@lingui/core";
import * as Collapsible from "@radix-ui/react-collapsible";
import { DropdownMenuCheckboxItemProps } from "@radix-ui/react-dropdown-menu";
import styles from "./QuickSubscribe.module.css";
import { BellDotIcon, ChevronDownIcon } from "lucide-react";

type QuickSubscribeProps = {
  className?: string;
  user_id: number;
  organization_uuid: string;
  search_uuid: string;
  allowedIntervals?: SearchSubscriptionInterval[];
};

const labels = {
  [SEARCH_SUBSCRIPTION_INTERVAL.REALTIME]:
    L.SEARCH_SUBSCRIPTION_INTERVAL_REALTIME,
  [SEARCH_SUBSCRIPTION_INTERVAL.DAILY]: L.SEARCH_SUBSCRIPTION_INTERVAL_DAILY,
  [SEARCH_SUBSCRIPTION_INTERVAL.WEEKLY]: L.SEARCH_SUBSCRIPTION_INTERVAL_WEEKLY,
  [SEARCH_SUBSCRIPTION_INTERVAL.MONTHLY]:
    L.SEARCH_SUBSCRIPTION_INTERVAL_MONTHLY,
  [SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_NEGATIVE]:
    L.SEARCH_SUBSCRIPTION_INTERVAL_REALTIME_INSIGHTS_NEG,
  [SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_POSITIVE]:
    L.SEARCH_SUBSCRIPTION_INTERVAL_REALTIME_INSIGHTS_POS,
  [SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_HIGH_REACH]:
    L.SEARCH_SUBSCRIPTION_INTERVAL_REALTIME_INSIGHTS_HIGH_REACH,
};

const Item = ({
  children,
  interval,
  ...rest
}: {
  children: React.ReactNode;
  interval: SearchSubscriptionInterval;
} & DropdownMenuCheckboxItemProps) => {
  return (
    <DropdownCheckboxItem
      key={SEARCH_SUBSCRIPTION_INTERVAL.REALTIME}
      onSelect={(e) => {
        // Don't close the menu when an item is clicked
        e.preventDefault();
      }}
      {...rest}
      data-testid={`quick-subscribe-${interval}`}
    >
      <DropdownItemIndicator forceMount>
        <CheckboxIndicator />
      </DropdownItemIndicator>
      {children}
    </DropdownCheckboxItem>
  );
};

const MetaItem = ({
  subscriptions,
  subscribe,
  unsubscribe,
  isSaving,
  allowedIntervals,
}: {
  subscriptions: ReturnType<
    typeof useSubscriptionHandler
  >["subscriptionsByInterval"];
  unsubscribe: ReturnType<typeof useSubscriptionHandler>["unsubscribe"];
  subscribe: ReturnType<typeof useSubscriptionHandler>["subscribe"];
  isSaving: boolean;
  allowedIntervals?: SearchSubscriptionInterval[];
}) => {
  const hasHighReachMentionsFeature = useHasFeature(
    FEATURE.HAS_HIGH_REACH_MENTIONS_FEATURE
  );

  const positiveSub =
    subscriptions[SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_POSITIVE];
  const negativeSub =
    subscriptions[SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_NEGATIVE];
  const highReachSub =
    subscriptions[SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_HIGH_REACH];

  const positiveSubscribed = positiveSub !== undefined;
  const negativeSubscribed = negativeSub !== undefined;
  const highReachSubscribed = highReachSub !== undefined;

  const isRestricted = (interval: SearchSubscriptionInterval) =>
    !!allowedIntervals && !allowedIntervals.includes(interval);

  const anyChildRestricted =
    isRestricted(SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_POSITIVE) ||
    isRestricted(SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_NEGATIVE) ||
    (hasHighReachMentionsFeature &&
      isRestricted(SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_HIGH_REACH));

  // parent shortcut only makes sense when all children are available
  if (anyChildRestricted) {
    return (
      <>
        <Item
          interval={SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_POSITIVE}
          disabled={
            isSaving ||
            isRestricted(
              SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_POSITIVE
            )
          }
          checked={positiveSubscribed}
          onCheckedChange={() => {
            if (positiveSub) unsubscribe(positiveSub);
            else
              subscribe(
                SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_POSITIVE
              );
          }}
        >
          {i18n._(
            labels[SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_POSITIVE]
          )}
        </Item>

        <Item
          interval={SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_NEGATIVE}
          disabled={
            isSaving ||
            isRestricted(
              SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_NEGATIVE
            )
          }
          checked={negativeSubscribed}
          onCheckedChange={() => {
            if (negativeSub) unsubscribe(negativeSub);
            else
              subscribe(
                SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_NEGATIVE
              );
          }}
        >
          {i18n._(
            labels[SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_NEGATIVE]
          )}
        </Item>

        {hasHighReachMentionsFeature && (
          <Item
            interval={SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_HIGH_REACH}
            disabled={
              isSaving ||
              isRestricted(
                SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_HIGH_REACH
              )
            }
            checked={highReachSubscribed}
            onCheckedChange={() => {
              if (highReachSub) unsubscribe(highReachSub);
              else
                subscribe(
                  SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_HIGH_REACH
                );
            }}
          >
            {i18n._(
              labels[SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_HIGH_REACH]
            )}
          </Item>
        )}
      </>
    );
  }

  let allSubscribed = positiveSubscribed && negativeSubscribed;
  let anySubscribed = positiveSubscribed || negativeSubscribed;

  if (hasHighReachMentionsFeature) {
    allSubscribed = allSubscribed && highReachSubscribed;
    anySubscribed = anySubscribed || highReachSubscribed;
  }

  return (
    <Collapsible.Root>
      <DropdownCheckboxItem
        key={SEARCH_SUBSCRIPTION_INTERVAL.REALTIME}
        checked={anySubscribed}
        disabled={isSaving}
        onSelect={(e) => {
          // Don't close the menu when an item is clicked
          e.preventDefault();
        }}
        data-testid="quick-subscribe-insights"
        onCheckedChange={() => {
          if (anySubscribed) {
            if (positiveSub) unsubscribe(positiveSub);
            if (negativeSub) unsubscribe(negativeSub);
            if (highReachSub) unsubscribe(highReachSub);
          } else {
            subscribe(SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_NEGATIVE);
            subscribe(SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_POSITIVE);
            if (hasHighReachMentionsFeature) {
              subscribe(
                SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_HIGH_REACH
              );
            }
          }
        }}
      >
        <DropdownItemIndicator forceMount>
          <CheckboxIndicator indecisive={!allSubscribed} />
        </DropdownItemIndicator>
        {L.SEARCH_SUBSCRIPTION_IMPORTANT_HEADER}
        <Collapsible.Trigger asChild>
          <Clickable
            onClick={(e) => e.stopPropagation()}
            style={{ pointerEvents: "auto" }}
          >
            <ChevronDownIcon className={styles.chevron} />
          </Clickable>
        </Collapsible.Trigger>
      </DropdownCheckboxItem>

      <Collapsible.Content>
        <Item
          className={styles.sub}
          interval={SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_POSITIVE}
          disabled={isSaving}
          checked={positiveSubscribed}
          onCheckedChange={() => {
            if (positiveSub) unsubscribe(positiveSub);
            else
              subscribe(
                SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_POSITIVE
              );
          }}
        >
          {i18n._(
            labels[SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_POSITIVE]
          )}
        </Item>

        <Item
          className={styles.sub}
          interval={SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_NEGATIVE}
          disabled={isSaving}
          checked={negativeSubscribed}
          onCheckedChange={() => {
            if (negativeSub) unsubscribe(negativeSub);
            else
              subscribe(
                SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_NEGATIVE
              );
          }}
        >
          {i18n._(
            labels[SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_NEGATIVE]
          )}
        </Item>

        {hasHighReachMentionsFeature && (
          <Item
            className={styles.sub}
            interval={SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_HIGH_REACH}
            disabled={isSaving}
            checked={highReachSubscribed}
            onCheckedChange={() => {
              if (highReachSub) unsubscribe(highReachSub);
              else
                subscribe(
                  SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_HIGH_REACH
                );
            }}
          >
            {i18n._(
              labels[SEARCH_SUBSCRIPTION_INTERVAL.REALTIME_INSIGHTS_HIGH_REACH]
            )}
          </Item>
        )}
      </Collapsible.Content>
    </Collapsible.Root>
  );
};

const QuickSubscribe: React.FunctionComponent<QuickSubscribeProps> = ({
  className,
  user_id,
  organization_uuid,
  search_uuid,
  allowedIntervals,
}) => {
  const {
    subscriptions,
    unsubscribe,
    unsubscribeAll,
    isSaving,
    isLoading,
    subscriptionsByInterval,
    subscribe,
  } = useSubscriptionHandler({
    organization_uuid,
    user_id,
    search_uuid,
  });

  const isSubscribed = subscriptions && subscriptions?.length > 0;
  const isDisabled = (interval: SearchSubscriptionInterval) =>
    isSaving || (!!allowedIntervals && !allowedIntervals.includes(interval));

  return (
    <Dropdown>
      <DropdownToggle asChild disabled={isLoading}>
        <Button
          data-testid="quick-subscribe"
          className={className}
          disabled={isLoading}
          variant="outline"
        >
          {isSubscribed
            ? L.REPORTS_QUICK_SUBSCRIBING
            : L.REPORTS_QUICK_SUBSCRIBE}
          {isSubscribed ? <BellDotIcon /> : <ChevronDownIcon />}
        </Button>
      </DropdownToggle>

      <DropdownMenu>
        <DropdownHeading>{L.REPORTS_QUICK_TITLE}</DropdownHeading>

        <Item
          interval={SEARCH_SUBSCRIPTION_INTERVAL.REALTIME}
          disabled={isDisabled(SEARCH_SUBSCRIPTION_INTERVAL.REALTIME)}
          checked={
            subscriptionsByInterval[SEARCH_SUBSCRIPTION_INTERVAL.REALTIME] !==
            undefined
          }
          onCheckedChange={() => {
            const sub =
              subscriptionsByInterval[SEARCH_SUBSCRIPTION_INTERVAL.REALTIME];
            if (sub) unsubscribe(sub);
            else subscribe(SEARCH_SUBSCRIPTION_INTERVAL.REALTIME);
          }}
        >
          {i18n._(labels[SEARCH_SUBSCRIPTION_INTERVAL.REALTIME])}
        </Item>

        <MetaItem
          subscribe={subscribe}
          unsubscribe={unsubscribe}
          isSaving={isSaving}
          allowedIntervals={allowedIntervals}
          subscriptions={subscriptionsByInterval}
        />

        <Item
          interval={SEARCH_SUBSCRIPTION_INTERVAL.DAILY}
          disabled={isDisabled(SEARCH_SUBSCRIPTION_INTERVAL.DAILY)}
          checked={
            subscriptionsByInterval[SEARCH_SUBSCRIPTION_INTERVAL.DAILY] !==
            undefined
          }
          onCheckedChange={() => {
            const sub =
              subscriptionsByInterval[SEARCH_SUBSCRIPTION_INTERVAL.DAILY];
            if (sub) unsubscribe(sub);
            else subscribe(SEARCH_SUBSCRIPTION_INTERVAL.DAILY);
          }}
        >
          {i18n._(labels[SEARCH_SUBSCRIPTION_INTERVAL.DAILY])}
        </Item>

        <Item
          interval={SEARCH_SUBSCRIPTION_INTERVAL.WEEKLY}
          disabled={isDisabled(SEARCH_SUBSCRIPTION_INTERVAL.WEEKLY)}
          checked={
            subscriptionsByInterval[SEARCH_SUBSCRIPTION_INTERVAL.WEEKLY] !==
            undefined
          }
          onCheckedChange={() => {
            const sub =
              subscriptionsByInterval[SEARCH_SUBSCRIPTION_INTERVAL.WEEKLY];
            if (sub) unsubscribe(sub);
            else subscribe(SEARCH_SUBSCRIPTION_INTERVAL.WEEKLY);
          }}
        >
          {i18n._(labels[SEARCH_SUBSCRIPTION_INTERVAL.WEEKLY])}
        </Item>

        <DropdownSeparator />

        <DropdownHeading>{L.REPORTS_QUICK_TITLE_MONTHLY}</DropdownHeading>
        <Item
          interval={SEARCH_SUBSCRIPTION_INTERVAL.MONTHLY}
          disabled={isDisabled(SEARCH_SUBSCRIPTION_INTERVAL.MONTHLY)}
          checked={
            subscriptionsByInterval[SEARCH_SUBSCRIPTION_INTERVAL.MONTHLY] !==
            undefined
          }
          onCheckedChange={() => {
            const sub =
              subscriptionsByInterval[SEARCH_SUBSCRIPTION_INTERVAL.MONTHLY];
            if (sub) unsubscribe(sub);
            else subscribe(SEARCH_SUBSCRIPTION_INTERVAL.MONTHLY);
          }}
        >
          {i18n._(labels[SEARCH_SUBSCRIPTION_INTERVAL.MONTHLY])}
        </Item>

        <DropdownSeparator />
        <DropdownCheckboxItem
          onSelect={() => unsubscribeAll()}
          data-testid="quick-subscribe-none"
          disabled={!isSubscribed || isSaving}
        >
          <UserMinus />
          {L.SEARCH_SUBSCRIPTION_END}
        </DropdownCheckboxItem>
      </DropdownMenu>
    </Dropdown>
  );
};

export default QuickSubscribe;
